/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    ButtonGroup,
    Divider,
    Stack,
    useDisclosure,
    Box,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {defineMessage, FormattedMessage, useIntl} from 'react-intl'
import {useItemVariant} from '@salesforce/retail-react-app/app/components/item-variant'
import ConfirmationModal from '../../../components/confirmation-modal'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
// import {DeleteIcon} from '../../../components/icons'
import useUtils from '../../../custom-hooks/use-utils-helper'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../../custom-api/layers/default'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {getPriceData} from '../../../utils/product-utils'

export const REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Confirm Remove Item',
        id: 'confirmation_modal.remove_cart_item.title.confirm_remove',
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Are you sure you want to remove this item from your cart?',
        id: 'confirmation_modal.remove_cart_item.message.sure_to_remove',
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.action.yes',
    }),
    primaryActionAriaLabel: defineMessage({
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.yes',
    }),
    alternateActionLabel: defineMessage({
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.action.no',
    }),
    alternateActionAriaLabel: defineMessage({
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.no',
    }),
    onPrimaryAction: noop,
}

/**
 * Renders secondary actions on a product-item card in the form of a button group.
 * Represents other actions you want the user to perform with the product-item
 * (eg.: Remove or Edit or Add to wishlist for cart items)
 */
const CartSecondaryButtonGroup = ({
    onAddToWishlistClick = noop,
    // hasBonusProduct,
    // onEditClick = noop,
    onRemoveItemClick = noop,
    productData,
}) => {
    const variant = useItemVariant()
    const {currency} = useCurrency()
    const {data: customer} = useCurrentCustomer()
    const {addLayer} = useGoogleTagManager()
    const modalProps = useDisclosure()
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('CartSecondaryButtonGroup', {})

    const handleRemoveItem = async () => {
        await addLayer(polish_ecommerce_gtm_event.bind(this))
        await addLayer(
            item_gtm_event.bind(this, {
                event: 'remove_from_cart',
                currency: currency,
                bundle: 'false',
                items: [
                    {
                        item_name: productData?.c_defaultName,
                        item_id: productData?.productId,
                        item_category: productData?.c_defaultCategoryName,
                        item_variant: productData?.c_subModelIdentifier,
                        price: parseFloat(
                            (
                                (productData?.price - productData?.tax) /
                                productData?.quantity
                            ).toFixed(2),
                        ),
                        fullprice: getPriceData(productData)?.isOnSale ? 'reduced' : 'fullprice',
                        rating_points: '', //?Value undecided or missing
                        erp_category: productData?.c_MaterialGroup,
                        prdha_category: productData?.c_prdhaCategory,
                        quantity: productData?.quantity,
                    },
                ],
            }),
        )
        onRemoveItemClick(variant)
    }

    const {isCart} = useUtils().currentPage(location.pathname)

    return (
        <>
            <Stack
                {...styles?.outerStack}
                divider={<Divider display={{base: 'block', lg: 'none'}} />}
            >
                <Box as="span">
                    <ButtonGroup spacing="6">
                        {/* <Button variant="link" size="sm" onClick={showRemoveItemConfirmation}>
                            <FormattedMessage
                                defaultMessage="Remove"
                                id="cart_secondary_button_group.action.remove"
                            />
                        </Button> */}

                        {/* This has been moved to the quantity picker component, so we can remove it from here.  */}
                        {/* {!hasBonusProduct && (
                            <Button
                                variant="ghost-not-rounded"
                                onClick={handleRemoveItem}
                                data-testid="cart-item-remove-button"
                                className="js-personalization-remove-button"
                                data-product-code={productData?.productId}
                                aria-label={formatMessage({
                                    id: 'cart_secondary_button_group.action.remove.aria_label',
                                    defaultMessage: 'Remove  from cart',
                                })}
                            >
                                <DeleteIcon
                                    {...styles?.deleteIcon}
                                    data-product-code={productData?.productId}
                                    role="img"
                                    aria-hidden="true"
                                />
                            </Button>
                        )} */}

                        {customer.isRegistered && !isCart && (
                            <Button
                                variant="link"
                                size="sm"
                                onClick={() => onAddToWishlistClick(variant)}
                                aria-label={formatMessage({
                                    id: 'cart_secondary_button_group.action.wishlist.aria_label',
                                    defaultMessage: 'Add {productName} to wishlist',
                                    values: {
                                        productName: productData?.c_defaultName || 'item',
                                    },
                                })}
                            >
                                <FormattedMessage
                                    defaultMessage="Add to Wishlist"
                                    id="cart_secondary_button_group.action.added_to_wishlist"
                                />
                            </Button>
                        )}
                        {/* <Button variant="link" size="sm" onClick={() => onEditClick(variant)}>
                            <FormattedMessage
                                defaultMessage="Edit"
                                id="cart_secondary_button_group.action.edit"
                            />
                        </Button> */}
                    </ButtonGroup>
                </Box>
            </Stack>
            <ConfirmationModal
                {...REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG}
                onPrimaryAction={handleRemoveItem}
                {...modalProps}
            />
        </>
    )
}

CartSecondaryButtonGroup.propTypes = {
    onClick: PropTypes.func,
    onEditClick: PropTypes.func,
    onAddToWishlistClick: PropTypes.func,
    onRemoveItemClick: PropTypes.func,
    onIsAGiftChange: PropTypes.func,
    isAGift: PropTypes.bool,
    hasBonusProduct: PropTypes.bool,
    productData: PropTypes.object,
}

export default CartSecondaryButtonGroup
